import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { parseDateOrUndefined } from '../util/date'
import ChecklistItem from './ChecklistItem'
import ChecklistSection from './ChecklistSection'
import Store from './Store'
import { ProviderChecklist } from '../providers/types'

export default class Checklist {
  id = ''
  name = ''
  templateId = ''
  assigneeId = ''
  creatorId = ''
  complete = false
  deadline?: Date | null = null
  completed_at?: Date | null = null
  created_at?: Date | null = null
  items = observable.array<ChecklistItem>()
  sections: ChecklistSection[] = []
  store: Store

  constructor(store: Store) {
    makeObservable(this, {
      name: observable,
      complete: observable,
      completed_at: observable,
      deadline: observable,
      assigneeId: observable,
      allDone: computed,
      sortedItems: computed,
      created_at: false,
      setAsComplete: action,
      fromProvider: action,
      assign: action,
      addItem: action,
      items: observable,
    })
    this.store = store
  }

  get allDone() {
    return this.items.every((i) => i.complete || i.skipped)
  }

  get sortedItems() {
    return this.items
      .slice()
      .sort((a: ChecklistItem, b: ChecklistItem) => a.position - b.position)
  }

  itemsWithoutSection() {
    return this.items.filter((item) => !item.sectionId)
  }

  fromProvider = (data: ProviderChecklist) => {
    this.id = data.id
    this.name = data.name
    this.complete = data.complete
    this.deadline = parseDateOrUndefined(data.deadline) || null
    this.completed_at = parseDateOrUndefined(data.completedAt) || null
    this.created_at = new Date(data.createdAt)
    this.creatorId = data.creatorId || ''
    this.assigneeId = data.assigneeId || ''
    this.templateId = data.templateId || ''
    this.items.replace(
      data.items.map((item) => {
        const converted = new ChecklistItem(this.store, this)
        converted.fromProvider(item)
        return converted
      })
    )
    this.sections = data.sections.map((section) => {
      const converted = new ChecklistSection(this.store, this, section.name)
      converted.fromProvider(section)
      return converted
    })
  }

  setAsComplete = async () => {
    this.complete = true
    this.completed_at = new Date()
    void this.store.provider.updateChecklist(this.id, {
      complete: true,
      completedAt: this.completed_at.toISOString(),
    })
  }

  rerun = async () => {
    return this.store.rerunChecklist(this.id)
  }

  buildItem = (name: string) => {
    const item = new ChecklistItem(this.store, this)
    item.name = name
    return item
  }

  addItem = (name: string) => {
    const item = ChecklistItem.create(this, name)
    this.items.push(item)
    return item
  }

  delete = async () => {
    return this.store.provider.deleteChecklist(this.id)
  }

  deleteItem = async (itemId: string) => {
    await this.store.provider.deleteChecklistItem(this.id, itemId)

    runInAction(() => {
      this.items.replace(this.items.filter((item) => item.id !== itemId))
    })
  }

  assign = async (userId: string | null) => {
    this.assigneeId = userId || ''
    const result = await this.store.provider.updateChecklist(this.id, {
      assigneeId: userId,
    })
    this.fromProvider(result)
  }

  async update(payload: { name: string }) {
    this.name = payload.name
    return this.store.provider
      .updateChecklist(this.id, {
        name: this.name,
      })
      .then((data) => this.fromProvider(data))
      .catch((e) => {
        // this.setLastError("Failed to create")
        throw e
      })
      .finally(() => {
        // this.setSaving(false)
      })
  }
}
